import { 
  AudioIconService, 
  AudioCategory, 
  AudioIconServiceDependencies
} from './types';

export class AudioIconServiceImpl implements AudioIconService {
  private readonly bucket = 'asset_cover_images';

  constructor(private readonly dependencies: AudioIconServiceDependencies) {}

  /**
   * Get a random audio icon URL from the specified category folder
   * @param category - The audio category (dialogue, sfx, music)
   * @returns Promise<string | null> - The URL of the randomly selected icon or null if failed
   */
  async getRandomAudioIcon(category: AudioCategory): Promise<string | null> {
    try {
      // Map category to folder path
      const folderPath = this.getCategoryFolderPath(category);
      
      // List all files in the category folder using injected storage provider
      const { data: files, error } = await this.dependencies.storage
        .from(this.bucket)
        .list(folderPath);
      
      if (error) {
        console.error('🎵 Failed to list files in folder:', error);
        return null;
      }

      if (!files || files.length === 0) {
        console.warn('🎵 No icons found in folder:', folderPath);
        return null;
      }

      // Filter for SVG files only
      const svgFiles = files.filter((file: any) => file.name.toLowerCase().endsWith('.svg'));
      
      if (svgFiles.length === 0) {
        console.warn('🎵 No SVG icons found in folder:', folderPath);
        return null;
      }

      // Randomly select one SVG file
      const randomIndex = Math.floor(Math.random() * svgFiles.length);
      const selectedFile = svgFiles[randomIndex];
      
      // Get the public URL for the selected file
      const { data: { publicUrl } } = this.dependencies.storage
        .from(this.bucket)
        .getPublicUrl(`${folderPath}/${selectedFile.name}`);
      
      if (!publicUrl) {
        console.error('🎵 Failed to get public URL for selected icon');
        return null;
      }

      return publicUrl;
    } catch (error) {
      console.error('🎵 Error getting random audio icon:', error);
      return null;
    }
  }

  /**
   * Get the Supabase storage folder path for a given audio category
   * @param category - The audio category
   * @returns string - The folder path
   */
  private getCategoryFolderPath(category: AudioCategory): string {
    const categoryMap: Record<AudioCategory, string> = {
      'dialogue': 'audio_cover_images/dialogue',
      'sfx': 'audio_cover_images/sound_effects',
      'music': 'audio_cover_images/music'
    };
    
    return categoryMap[category];
  }

  /**
   * Get the tag name for a given audio category
   * @param category - The audio category
   * @returns string - The tag name to add to asset type tags
   */
  getCategoryTag(category: AudioCategory): string {
    const tagMap: Record<AudioCategory, string> = {
      'dialogue': 'dialogue',
      'sfx': 'sound_effects',
      'music': 'music'
    };
    
    return tagMap[category];
  }
} 